export interface User {
  id: string
  email: string
  name: string
  isActive: boolean
  roles: UserRole[]
  teams: { id: string; name: string }[]
  positions: { id: string; name: string }[]
  brands: { id: string; name: string }[]
}

export type UserRole =
  | 'ai_admin'
  | 'brand_content_owner'
  | 'team_lead'
  | 'contributor'
  | 'viewer'
  | 'security_compliance'

// Agent types -- matches API contract from CLIENT_AGENT_GUIDE.md

export type AgentStatus = 'draft' | 'in_review' | 'published' | 'deprecated' | 'archived'
export type AgentCategory = 'product' | 'marketing' | 'operations' | 'general'

export interface DashboardStats {
  active_agents: number
  weekly_conversations: number
  avg_satisfaction: number | null
  pending_approvals: number
}

export interface RecentAgent {
  id: string
  name: string
  description: string | null
  category: AgentCategory
  status: AgentStatus
  ownerUserId: string
  ownerEmail: string | null
  ownerName: string | null
  updatedAt: string
  conversationCount: number
  satisfactionScore: number | null
}

export interface Agent {
  id: string
  name: string
  description: string | null
  brandId: string | null
  category: AgentCategory
  ownerUserId: string
  ownerEmail: string | null
  ownerName: string | null
  status: AgentStatus
  promptConfig: {
    system: string
    style: string
    guardrails: string
    outputFormat: string
  }
  generalContext: string | null
  currentVersionNumber: number
  templateKey?: string   // NEW — D-01. Optional until agents-hub-api A7 surfaces it on the response.
  createdAt: string
  updatedAt: string
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

// Agent detail types -- Phase 3

export interface AgentVersion {
  id: string
  agentId: string
  versionNumber: number
  status: AgentStatus
  promptConfig: {
    system: string
    style: string
    guardrails: string
    outputFormat: string
  }
  generalContextSnapshotId: string | null
  createdBy: string | null
  approvedBy: string | null
  createdAt: string
}

export interface AccessPolicy {
  id: string
  agentId: string
  allowedRoles: string[]
  allowedTeams: string[]
  allowedPositions: string[]
}

export interface KnowledgePackLink {
  id: string
  name: string
  description: string | null
  status: string
}

export interface UpdateAgentPayload {
  name?: string
  description?: string | null
  brandId?: string | null
  category?: AgentCategory
  promptConfig?: {
    system: string
    style: string
    guardrails: string
    outputFormat: string
  }
  generalContext?: string | null
}

export interface CreateAgentPayload {
  name: string
  description?: string
  brandId?: string
  category?: AgentCategory
  templateKey: string
  promptConfig?: {
    system: string
    style: string
    guardrails: string
    outputFormat: string
  }
}

// Knowledge Pack types -- Phase 5

export type KnowledgePackStatus = 'pending' | 'processing' | 'ready' | 'partial' | 'failed'
export type FileProcessingStatus =
  | 'pending'
  | 'extracting'
  | 'chunking'
  | 'embedding'
  | 'ready'
  | 'failed'
  | 'updating'         // NEW — Phase 27 (API Phase 24): set synchronously before update-from-source returns 202
  | 'update_failed'    // NEW — Phase 27 (API Phase 24): set by worker on update-mode failure; old chunks preserved

// File source provenance — matches agents-hub-api Phase 22 contract (2026-04-19).
// See ../AGENTS.md "Phase 22 shipped — consumed contracts" for the canonical shape.
/**
 * File source state — server-derived at read time.
 * - `in_sync`: imported file matches upstream version
 * - `source_updated`: upstream has newer version (Phase 25 amber pill)
 * - `source_deleted`: upstream removed (Phase 25 / Phase 27)
 * - `source_disconnected`: connector credentials removed (Phase 28 — desaturated badge).
 *   Derived server-side at read time when `sourceType !== 'local' && connectorId === null && externalId !== null`.
 */
export type FileSourceState =
  | 'in_sync'
  | 'source_updated'
  | 'source_deleted'
  | 'source_disconnected'

export interface FileSource {
  connectorId: string | null
  connectorType: ConnectorProvider | null
  externalId: string
  externalName: string
  externalUrl: string | null
  sourceVersion: string
  sourceState: FileSourceState
  lastSyncedAt: string
}

export interface KnowledgePackFile {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  status: FileProcessingStatus
  /**
   * Populated when status === 'update_failed' (or 'failed') with a short
   * code from the API: IMPORT_FILE_NOT_FOUND, IMPORT_FILE_TOO_LARGE,
   * s3_overwrite_failed, credentials_decrypt_failed, chunk_swap_failed,
   * handoff_enqueue_failed, enqueue_failed. 200-char truncated server-side.
   * `null` for healthy rows.
   */
  errorMessage: string | null
  /**
   * Phase 38 Plan 02 (G10): per-file count of embedded images that the
   * text extractor did NOT OCR (PDF paintImageXObject ops, office word/media/
   * entries). Non-nullable — API ships `Type.Integer({ minimum: 0 })` and
   * Migration 0013 sets DB DEFAULT 0 so every row has an integer value.
   * The "N image(s) not indexed (OCR coming)" subtitle is rendered only
   * when status === 'ready' && imagesSkipped > 0.
   */
  imagesSkipped: number
  createdAt: string
  updatedAt: string
  source: FileSource | null
}

export interface KnowledgePack {
  id: string
  name: string
  description: string | null
  type: string | null
  sourceRef: string | null
  status: KnowledgePackStatus
  stalenessThresholdDays: number
  totalFiles: number
  totalSizeBytes: number
  totalChunks: number
  isStale: boolean
  daysSinceUpdate: number
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
  /**
   * Phase 28 (KSY-01): per-KP toggle that opts the pack into the server-side
   * pg-boss `auto-sync-kp` job (cadence env-driven, default 15 min).
   * Always present on the response; default `false` for existing rows
   * (backfilled by API migration `0008`).
   */
  autoSyncExternalFiles: boolean
  files?: KnowledgePackFile[]
}

export interface KnowledgePackStats {
  totalPacks: number
  byStatus: Record<KnowledgePackStatus, number>
  totalFiles: number
  totalSizeBytes: number
  totalChunks: number
}

export interface CreateKnowledgePackPayload {
  name: string
  description?: string
}

export interface UpdateKnowledgePackPayload {
  name?: string
  description?: string | null
  type?: string | null
  sourceRef?: string | null
  stalenessThresholdDays?: number
  /**
   * Phase 28 (KSY-02): toggle auto-sync on or off for this pack.
   * Idempotent on same-value PATCH per API §1.
   */
  autoSyncExternalFiles?: boolean
}

export interface UploadUrlResponse {
  fileId: string
  uploadUrl: string
  s3Key: string
}

export interface AgentPackLink {
  id: string
  agentId: string
  knowledgePackId: string
  linkedAt: string
  linkedBy: string | null
}

export interface LinkedAgent {
  id: string
  name: string
  category: string
  status: string
}

// Phase 28 (KSY-03/04/05): connector disconnect cascade vs orphan strategy.
// API Phase 25 §3 — discriminated union response keyed on `strategy`.

/**
 * Disconnect strategy chosen in the DisconnectDialog.
 * - `cascade`: remove imported files across all KPs that referenced this connector
 * - `orphan` (default per KSY-04): keep imported files, flip them to `source_disconnected`
 */
export type DisconnectStrategy = 'cascade' | 'orphan'

/**
 * Discriminated union returned from POST /api/v1/connectors/{provider}/disconnect.
 * Narrow on `response.strategy` to access branch-specific count fields.
 * - cascade branch: `deletedFiles` + `affectedPacks` + per-file `errors[]`
 * - orphan branch:  `orphanedFiles` + `affectedPacks` (errors not applicable — no destructive op)
 */
export type DisconnectResponse =
  | {
      success: true
      strategy: 'cascade'
      deletedFiles: number
      affectedPacks: number
      errors: Array<{ fileId: string; code: string; message: string }>
    }
  | {
      success: true
      strategy: 'orphan'
      orphanedFiles: number
      affectedPacks: number
    }

/**
 * Live impact preview returned from GET /api/v1/connectors/{provider}/disconnect-preview.
 * Pure DB COUNT query server-side — cheap; safe to fetch every dialog open with `staleTime: 0`.
 */
export interface DisconnectPreview {
  fileCount: number
  packCount: number
}

// Conversation types -- Phase 7 (v2.0)

export type ConversationStatus = 'active' | 'archived'
export type ConversationRating = 'thumbs_up' | 'thumbs_down'
export type MessageRole = 'user' | 'assistant' | 'system'

export interface ConversationMessage {
  id: string
  role: MessageRole
  content: string
  inputTokens: number | null
  outputTokens: number | null
  model: string | null
  provider: string | null
  cost: string | null
  latencyMs: number | null
  createdAt: string
  // Present when an assistant turn emitted a PRD draft (v4.2 Phase 25). Three states:
  //   - string   → assistant turn emitted a PRD draft; navigate via /agents/$agentId/drafts/$draftId
  //   - null     → post-A6 server explicitly returned null (non-emitting turn or non-PRD agent)
  //   - undefined → pre-A6 server that does not yet include this field in conversation responses
  // Consumers must truthy-check (e.g. `msg.draftId ? ... : null`) to cover all three. See
  // the agents-hub-api AGENTS.md, item A6, for the server-side rollout.
  draftId?: string | null
}

export interface Conversation {
  id: string
  agentId: string
  agentVersionId: string | null
  userId: string
  title: string | null
  status: ConversationStatus
  isSandbox: boolean
  rating: ConversationRating | null
  createdAt: string
  updatedAt: string
  messages?: ConversationMessage[]
}

export interface ConversationListParams {
  agent_id?: string
  user_id?: string
  status?: ConversationStatus
  rating?: ConversationRating
  is_sandbox?: boolean
  search?: string
  from?: string
  to?: string
  page?: number
  page_size?: number
  sort_by?: 'created_at' | 'updated_at'
  sort_order?: 'asc' | 'desc'
}

export interface ConversationExportParams {
  format?: 'json' | 'csv'
  agent_id?: string
  user_id?: string
  status?: ConversationStatus
  from?: string
  to?: string
}

export interface RateConversationPayload {
  rating: ConversationRating | null
}

// Analytics types -- Phase 7 (v2.0)

export type AnalyticsPeriod = '7d' | '30d' | '90d'

export interface AnalyticsOverview {
  totalConversations: number
  activeUsers: number
  avgResponseTimeMs: number | null
  activeAgents: number
  categoryDistribution: { category: string; count: number }[]
  totalCost: string
  satisfactionRate: number | null
  totalRated: number
  failureRate: number | null
}

export interface AgentAnalytics {
  agentId: string
  agentName: string
  conversationCount: number
  messageCount: number
  avgResponseTimeMs: number | null
  satisfactionRate: number | null
  totalRated: number
  totalCost: string
  totalInputTokens: number
  totalOutputTokens: number
  topModels: { model: string | null; count: number }[]
}

export interface AnalyticsTrends {
  period: string
  dataPoints: {
    date: string
    conversations: number
    satisfactionRate: number | null
    cost: string
  }[]
}

// Governance types -- Phase 7 (v2.0)

export interface AuditLogEntry {
  id: string
  eventType: string
  actorId: string
  objectType: string
  objectId: string
  beforeJson: unknown | null
  afterJson: unknown | null
  ipAddress: string | null
  createdAt: string
}

export interface AuditLogListParams {
  event_type?: string
  actor_id?: string
  object_type?: string
  object_id?: string
  from?: string
  to?: string
  page?: number
  page_size?: number
}

export interface ApprovalQueueItem {
  id: string
  name: string
  description: string | null
  category: string
  brandId: string | null
  ownerUserId: string
  submittedForReviewAt: string | null
  hoursInReview: number
}

export interface ApprovalQueueParams {
  page?: number
  page_size?: number
}

// SSE event types -- Phase 7 (v2.0)

export interface SSETokenEvent {
  event: 'token'
  data: { content: string }
}

export interface SSESourcesEvent {
  event: 'sources'
  data: {
    sources: {
      sourceIndex: number
      packName: string
      fileName: string
      chunkIndex: number
      similarity: number
    }[]
  }
}

export interface SSEDoneEvent {
  event: 'done'
  data: {
    usage: { inputTokens: number; outputTokens: number }
    latency_ms: number
    cost: string
    // Optional — API may include these in future
    conversationId?: string
    messageId?: string
    // Present only when the assistant turn persisted a PRD draft (v4.2 Phase 25)
    draftId?: string
    draftTitle?: string
  }
}

export interface SSEErrorEvent {
  event: 'error'
  data: { code: string; detail: string; status: number; title: string }
}

export type SSEEvent = SSETokenEvent | SSESourcesEvent | SSEDoneEvent | SSEErrorEvent

// Execution types -- Phase 20 (v4.0)

export type ExecutionRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
export type ExecutionRunTrigger = 'manual' | 'schedule' | 'conversation'

export interface ExecutionRun {
  id: string
  agentId: string
  status: ExecutionRunStatus
  triggeredBy: ExecutionRunTrigger
  triggeredByUserId: string | null
  triggeredByUsername: string | null
  startedAt: string | null
  completedAt: string | null
  totalTokens: number
  totalImageCount: number
  totalSearchCredits: number
  error: string | null
  createdAt: string
}

export interface ExecutionStep {
  id: string
  runId: string
  stepName: string
  stepType: string
  status: string
  durationMs: number | null
  metadata: Record<string, unknown>
  error: string | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
}

export interface ExecutionDetail extends ExecutionRun {
  steps: ExecutionStep[]
}

export interface ExecutionListResponse {
  data: ExecutionRun[]
  pagination: {
    nextCursor: string | null
    hasMore: boolean
  }
}

export interface TriggerExecutionResponse {
  runId: string
  status: string
}

export interface CancelExecutionResponse {
  runId: string
  status: string
}

// Phase 29 (v4.3) — Conversation-driven execution synthesis.
// Cross-repo contract: agents-hub-api Phase 29 (`feature/phase-29-conversation-driven-execution-synthesis`).
// See ../AGENTS.md §"v4.1 In Progress / Phase 29" for the full round-trip.

/** POST /api/v1/agents/:id/synthesize-execution-config request body. */
export interface SynthesizeExecutionConfigRequest {
  conversationId: string
}

/**
 * 200 response — exact shape of `ExecutionConfigInputSchema` on the server.
 * Used as both the API response and the form's pre-fill payload.
 */
export interface SynthesizedExecutionConfig {
  topic: string
  keywords: string[]
  tone?: string
  targetWordCount?: number
}

/**
 * Body for POST /api/v1/agents/:id/execute when triggered from a conversation.
 * Audit invariant: `triggerSource === 'conversation'` IFF `sourceConversationId`
 * present. Enforced via discriminated union (compile time) + buildExecuteBody
 * runtime check (mutations/execution.ts). Tab flow continues to call zero-arg.
 */
export type RunExecutionPayload =
  & SynthesizedExecutionConfig
  & { triggerSource: 'conversation'; sourceConversationId: string }

export interface PromptSuggestionsResponse {
  suggestions: string[]
  currentPrompt: string | null
}

export interface Schedule {
  id: string
  agentId: string
  cronExpression: string
  timezone: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface UpsertSchedulePayload {
  cronExpression: string
  timezone?: string
  enabled?: boolean
}

// PRD Draft types -- dedicated prd_drafts table (replaces blog_drafts reuse).

// Aligns with API migration 0017_breezy_namora — 'approved' was sunset and
// 'published' became the explicit user-facing milestone. 'exported' is set
// orthogonally by the Drive/ClickUp export paths.
export type PrdDraftStatus = 'draft' | 'rejected' | 'exported' | 'published'

export interface PrdDraftListItem {
  id: string
  agentId: string
  title: string
  status: PrdDraftStatus
  exportedToDriveAt: string | null
  exportedToClickUpAt: string | null
  createdAt: string
  updatedAt: string
}

export interface PrdDraft {
  id: string
  agentId: string
  conversationId: string | null
  messageId: string | null
  title: string
  body: string
  status: PrdDraftStatus
  createdBy: string | null
  // No rejection_reason column on prd_drafts yet — surfaced as null so existing
  // UI (PrdDraftDetailPage rejection-callout) renders nothing when rejected.
  rejectionReason: string | null
  exportedToDriveAt: string | null
  driveFileUrl: string | null
  exportedToClickUpAt: string | null
  clickUpTaskUrl: string | null
  createdAt: string
  updatedAt: string
}

export interface PrdDraftListResponse {
  data: PrdDraftListItem[]
  pagination: {
    nextCursor: string | null
    hasMore: boolean
  }
}

// Blog Draft types -- Phase 21 (v4.0)

export type DraftStatus =
  | 'draft'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'scheduled'
  | 'exported' // PRD-only — status flips to this after a successful Drive/ClickUp export.

export type PublishStatus = 'pending' | 'processing' | 'failed' | 'succeeded'

// Phase 30 (v4.3) -- Web Source Provenance.
// Per AGENTS.md contract: when present the field is always an array
// (empty when no web sources were consumed), never null. Optional on the
// type until the API row in AGENTS.md flips to prod-shipped.
export type DraftSource = {
  url: string
  title: string
  fetchedAt: string
}

export interface BlogDraft {
  id: string
  agentId: string
  executionRunId: string | null
  title: string
  body: string
  status: DraftStatus
  heroImageUrl: string | null
  heroImageAlt: string | null
  selectedImageUrl: string | null
  seo: {
    title: string
    description: string
    keywords: string[]
  }
  rejectionReason: string | null
  scheduledAt: string | null
  publishStatus: PublishStatus | null
  publishError: string | null
  webflowItemId: string | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
  sources?: DraftSource[]
}

export interface ImageCandidate {
  id: string
  draftId: string
  prompt: string
  imageUrl: string
  mimeType: string
  sizeBytes: number | null
  selected: boolean
  generationRound: number
  createdAt: string
}

export interface ImageCandidatesResponse {
  data: ImageCandidate[]
  totalCount: number
}

export interface DraftListParams {
  status?: DraftStatus
  page?: number
  page_size?: number
}

export interface UpdateDraftPayload {
  title?: string
  body?: string
  heroImageUrl?: string | null
  heroImageAlt?: string | null
  seo?: {
    title?: string
    description?: string
    keywords?: string[]
  }
}

// Connector types -- Phase 14 (v3.0)

export type ConnectorProvider = 'google_drive' | 'clickup'

/** Maps ConnectorProvider to the API route path segment */
export const providerRouteSegment: Record<ConnectorProvider, string> = {
  google_drive: 'google',
  clickup: 'clickup',
}

export type ConnectorStatus = 'active' | 'inactive' | 'error'

export interface Connector {
  id: string
  name: string
  type: ConnectorProvider
  /**
   * Authoritative OAuth-complete gate per API Phase 21. When false, the
   * connector row should render "Auth incomplete" and offer "Complete OAuth".
   */
  hasCredentials: boolean
  status: ConnectorStatus
  lastSyncAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateConnectorPayload {
  name: string
  type: ConnectorProvider
}

// Google Drive types -- Phase 15 (v3.0)

export interface DriveItem {
  id: string
  name: string
  mimeType: string
  size: number | null
  childCount: number | null
  modifiedAt: string
}

export type BrowseResponse = DriveItem[]

export interface OAuthConnectResponse {
  authUrl: string
}

export interface OAuthCallbackPayload {
  type: 'oauth-callback'
  success: boolean
  error?: string
}

// ClickUp types -- Phase 16 (v3.0)

export type ClickUpNodeType = 'workspace' | 'space' | 'folder' | 'list' | 'doc'

export interface ClickUpNode {
  id: string
  name: string
  type: ClickUpNodeType
  childCount?: number
  updatedAt?: string
  hasChildren?: boolean
}

export type ClickUpBrowseResponse = ClickUpNode[]

// Insights types -- Phase 18 (v3.0)

export type FlagReason = 'low_confidence' | 'cannot_answer'

export interface FlaggedMessage {
  id: string
  content: string
  retrievalTopScore: string | null
  retrievalChunkCount: number | null
  cannotAnswer: boolean
  cannotAnswerReason: string | null
  createdAt: string
}

export interface FlaggedConversation {
  id: string
  agentId: string
  userId: string
  title: string | null
  createdAt: string
  flaggedMessages: FlaggedMessage[]
}

export interface FlaggedConversationsResponse {
  summary: {
    totalFlagged: number
    lowConfidenceCount: number
    cannotAnswerCount: number
  }
  data: FlaggedConversation[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}
